import { Component, Input, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { BookingData } from '../models/booking-data';
import { AdminBookingsService } from '../service/admin-bookings.service';
import { AppConstants } from '../AppConstants';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { BookedDetailsComponent } from '../booked-details/booked-details.component';
import { BookSelectedSlotsComponent } from '../book-selected-slots/book-selected-slots.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { AddToCartComponent } from '../add-to-cart/add-to-cart.component';
import { MatSnackBar } from '@angular/material/snack-bar';

// Interface to define the structure of venue booking data
interface VenueBookingData {
  venueName: string;
  availableSlots: any[];
  bookedSlots: any[];
}

@Component({
  selector: 'app-group-by-venue-booking-slots',
  templateUrl: './group-by-venue-booking-slots.component.html',
  styleUrls: ['./group-by-venue-booking-slots.component.css']
})
export class GroupByVenueBookingSlotsComponent implements OnInit {
  // Input properties from parent component
  @Input() showFullDay; // Flag to show full day slots
  @Input() selectedDate; // Currently selected date
  @Input() selectedProduct; // Currently selected product
  @Input() venues: any[]; // List of venues

  @ViewChild(MatMenuTrigger) slotMenuTrigger!: MatMenuTrigger;

  selectedSlot: any = null;

  // Constants and configuration
  dateRange = 7; // Number of days to show in the grid
  weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; // Array of weekday names
  months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']; // Array of month names
  dataSource = []; // Data source for the table
  origDataSource = []; // Original data source backup
  columnData = []; // Column definitions for the table
  dayColumnsObj = []; // Object containing day column information
  dayColumns = []; // Array of day column names
  slotDetails: any = {}; // Object to store slot details
  slotInterval = 60; // Duration of each slot in minutes
  bookingData: VenueBookingData[]; // Array to store booking data
  bookingSlotsSubscription: Subscription; // Subscription for booking slots
  user: any; // Current user information
  singleClickTimeout: any; // Timeout for single click handling
  isSingleClick = false; // Flag to track single click state
  rupeeSign = 'Rs.'; // Currency symbol
  reloadDataSubscription: Subscription; // Subscription for data reload
  selectedSlotRemovedSubscription: Subscription; // Subscription for slot removal
  venueDuration = { // Object to store venue timing information
    startTime: '',
    endTime: ''
  };
  clickTimeout: any = null;
  pageRefreshSubscriber: Subscription;
  isMobile = false;
  isLoading = false;

  constructor(private cd: ChangeDetectorRef, private dialog: MatDialog, private adminBookingSvc: AdminBookingsService, private snackBar: MatSnackBar) {

    this.pageRefreshSubscriber = this.adminBookingSvc.getRefreshPage().subscribe(res => {
      if (res) {
        this.initializeData();
      }
    })
  }

  // Lifecycle hook to handle input changes
  ngOnChanges(changes) {
    // Refresh grid when selected date or showFullDay changes
    if (changes && (changes.selectedDate && !changes.selectedDate.firstChange &&
      changes.selectedDate.previousValue !== changes.selectedDate.currentValue) ||
      (changes.showFullDay && !changes.showFullDay.firstChange &&
        changes.showFullDay.previousValue !== changes.showFullDay.currentValue)) {
      this.refreshGrid();
    }
    // Update slot interval and refresh grid when product changes
    if (changes && changes.selectedProduct && !changes.selectedProduct.firstChange &&
      changes.selectedProduct.previousValue !== changes.selectedProduct.currentValue) {
      this.slotInterval = this.selectedProduct.durationPerSlot;
      this.refreshGrid();
    }
  }

  // Helper method to add leading zero to single digit numbers
  addZeroIfRequired(value: number) {
    if (value > 9) {
      return value;
    }
    return '0' + value;
  }

  // Lifecycle hook for component initialization
  ngOnInit() {

    this.isMobile = window.innerWidth < 768;
    console.log("selectedProduct---", this.selectedProduct);
    this.slotInterval = this.selectedProduct.durationPerSlot;
    this.setAllDayHoursInSource();
    this.initializeData();
    // Subscribe to data reload events
    this.reloadDataSubscription = this.adminBookingSvc.reloadData.subscribe((data: any) => {
      if (data.reloadData) {
        this.refreshGrid();
      }
    });
    // Subscribe to slot removal events
    this.selectedSlotRemovedSubscription = this.adminBookingSvc.selectedSlotRemoved.subscribe((slotData: any) => {
      const element = this.dataSource.find(el => el.dateTime === slotData.dateTime);
      // this.showData(element, slotData.date, null, null, null);
      setTimeout(() => {
        this.refreshGrid();
      }, 100);
    });
  }

  // Initialize booking data from API
  initializeData() {
    if (this.selectedDate && this.selectedProduct) {
      this.bookingSlotsSubscription?.unsubscribe();
      this.isLoading = true;
      this.user = JSON.parse(sessionStorage.getItem(AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT));
      // Set tenant ID for admin users
      if (AppConstants.SESSION_STORAGE_ROLEID == 1) {
        this.user.tenantId = AppConstants.SESSION_STORAGE_TENANTID;
      }

      // Calculate date range for API call
      const endDate = this.addXNumberOfDays(this.selectedDate, 7);
      const startDateStr = this.selectedDate.getFullYear() + '-' + this.addZeroIfRequired(this.selectedDate.getMonth() + 1) + '-' +
        this.addZeroIfRequired(this.selectedDate.getDate());
      const endDateStr = endDate.getFullYear() + '-' + this.addZeroIfRequired(endDate.getMonth() + 1) + '-' + this.addZeroIfRequired(endDate.getDate());

      // Prepare API parameters
      const params = {
        tenantId: this.user.tenantId,
        productId: this.selectedProduct.id,
        startDate: startDateStr,
        endDate: endDateStr
      };

      // Call appropriate API based on product type
      if (this.selectedProduct.type === AppConstants.SINGLE_SESSION) {
        this.bookingSlotsSubscription = this.adminBookingSvc.getSingleSessionBookingSlots(params).subscribe((response: any) => {
          this.bookingData = this.getBookingDataForCurrentVenue(response, 'singleBookingAvailableSlots', 'singleBookingBookedSlots');
          this.renderGrid();
          this.isLoading = false;
        }, () => {
          this.isLoading = false;
        });
      } else {
        this.bookingSlotsSubscription = this.adminBookingSvc.getMultiSessionBookingSlots(params).subscribe((response: any) => {
          this.bookingData = this.getBookingDataForCurrentVenue(response, 'multiBookingAvailableSlots', 'multiBookingBookedSlots');
          this.renderGrid();
          this.isLoading = false;
        }, () => {
          this.isLoading = false;
        });
      }
    }
  }

  // Helper method to add specified number of days to a date
  addXNumberOfDays(startDate: Date, numberOfDays: number): Date {
    startDate.setHours(0, 0, 0, 0);
    const millisecondsInSevenDays = numberOfDays * 24 * 60 * 60 * 1000;
    const futureDate = new Date(startDate.getTime() + millisecondsInSevenDays);
    return futureDate;
  }

  // Process booking data to organize by venue
  // getBookingDataForCurrentVenue(bookingData: any[], availableSlots: string, bookedSlots: string): VenueBookingData[] {
  //   const venueDataMap = new Map<string, VenueBookingData>();

  //   if (bookingData && bookingData.length > 0) {
  //     bookingData.forEach((venueData) => {
  //       const venueName = venueData.venueName;
  //       if (!venueDataMap.has(venueName)) {
  //         venueDataMap.set(venueName, {
  //           venueName: venueName,
  //           availableSlots: [],
  //           bookedSlots: []
  //         });
  //       }
  //       const venueDataObj = venueDataMap.get(venueName);
  //       if (venueData[availableSlots]) {
  //         venueDataObj.availableSlots.push(...venueData[availableSlots]);
  //       }
  //       if (venueData[bookedSlots]) {
  //         venueDataObj.bookedSlots.push(...venueData[bookedSlots]);
  //       }
  //     });
  //   }

  //   return Array.from(venueDataMap.values());
  // }

  // Refresh the grid data

  getBookingDataForCurrentVenue(bookingData: any[], availableSlots: string, bookedSlots: string): VenueBookingData[] {
    const venueDataMap = new Map<string, VenueBookingData>();

    if (bookingData && bookingData.length > 0) {
      bookingData.forEach((venueData) => {
        const venueName = venueData.venueName;

        if (!venueDataMap.has(venueName)) {
          venueDataMap.set(venueName, {
            venueName: venueName,
            availableSlots: [],
            bookedSlots: []
          });
        }

        const venueDataObj = venueDataMap.get(venueName);

        // Ensure available slots are properly added
        if (venueData[availableSlots] && Array.isArray(venueData[availableSlots])) {
          venueDataObj.availableSlots.push(...venueData[availableSlots]);
        }

        // Ensure booked slots are properly added
        if (venueData[bookedSlots] && Array.isArray(venueData[bookedSlots])) {
          venueDataObj.bookedSlots.push(...venueData[bookedSlots]);
        }
      });
    }

    return Array.from(venueDataMap.values());
  }

  refreshGrid() {
    this.dataSource = [];
    this.setAllDayHoursInSource();
    this.initializeData();
  }

  setVenueDuration() {
    let allTimings: string[] = [];
    console.log("SettingVenueDuration");
    // Iterate through each booking data entry and collect timings
    this.bookingData.forEach((booking) => {
      if (booking.availableSlots) {
        allTimings.push(...booking.availableSlots.map((slot) => slot.startTime));
      }
      if (booking.bookedSlots) {
        allTimings.push(...booking.bookedSlots.map((slot) => slot.startTime));
      }
    });

    // Remove duplicates and sort timings
    const uniqueTimings = [...new Set(allTimings)];
    const sortedTimings = uniqueTimings.sort((a, b) => {
      const splitA = a.split(':');
      const splitB = b.split(':');
      return parseInt(splitA[0], 10) - parseInt(splitB[0], 10);
    });

    if (sortedTimings.length === 0) {
      console.warn("No timings available.");
      return;
    }

    this.venueDuration = {
      startTime: sortedTimings[0],
      endTime: sortedTimings[sortedTimings.length - 1]
    };

    // Filtering `dataSource` based on computed start and end time
    let newDataSource = [];
    let canAddData = true;

    this.dataSource.forEach((ds) => {
      if (
        canAddData &&
        (newDataSource.length > 0 && ds.dateTime !== this.venueDuration.endTime) ||
        ds.dateTime === this.venueDuration.startTime
      ) {
        newDataSource.push(ds);
      } else if (ds.dateTime === this.venueDuration.endTime) {
        newDataSource.push(ds);
        canAddData = false;
      }
    });

    this.dataSource = newDataSource;
    //console.log('New Data Source:', this.dataSource);
  }

  // Render the grid with slot data
  renderGrid() {
    if (!this.showFullDay) {
      this.setVenueDuration();
    }
    this.slotDetails = {};
    this.columnData = ['dateTime'];
    this.dayColumns = this.getDayColumns();
    this.columnData = this.columnData.concat(this.dayColumns);
    this.formSlotData();
    this.updateDataSource();
    this.cd.detectChanges();
  }

  // Handle single click on slot
  onSingleClick(element, dataKey, event, venue, action) {
    this.isSingleClick = true;
    this.singleClickTimeout = setTimeout(() => {
      if (this.isSingleClick) {
        this.showData(element, dataKey, event, venue, action);
      }
    }, 200);
  }

  // Process slot click and update selection
  showData(element, dataKey, event, venue, action) {
    const slotClicked = element ? element[dataKey]?.[venue] : null; // Accessing the slot for the specific venue
    if (!slotClicked) return;

    if (action == 'view') {
      // if (event && slotClicked.isBooked) {
      this.dialog.open(BookedDetailsComponent, {
        data: slotClicked,
        width: this.isMobile ? '95vw' : '600px',
        height: this.isMobile ? '90vh' : '650px',
        maxWidth: this.isMobile ? '95vw' : '600px'
        /* width: '600px',
        height: '600px' */
      });
      return;
      // }
    }

    // Add only single slot at a time logic
    else if (action == 'add') {
      let selectedSlots = JSON.parse(sessionStorage.getItem('selectedSlots') || '[]');

      // Guard: slotClicked and necessary properties must exist
      if (!slotClicked || !this.selectedProduct) {
        this.snackBar.open('Something went wrong. Please try again.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
        return;
      }

      // Guard: availableSlots check
      if (!Array.isArray(slotClicked.availableSlots) || slotClicked.availableSlots.length === 0) {
        this.snackBar.open('No available slots found for this selection.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
        return;
      }

      // MULTI SLOT CASE
      // if (this.selectedProduct.capacityPerVenue > 1) {
      //   const slotUniqueId = slotClicked.availableSlots[0]?.uniqueId;

      //   // Scenario 1: Booked slots exceed capacity
      //   if (Array.isArray(slotClicked.bookedSlots) && slotClicked.bookedSlots.length >= this.selectedProduct.capacityPerVenue) {
      //     console.log("Scenario 1 - Booked full");
      //     this.snackBar.open('This selected slot is fully booked!', 'Close', {
      //       duration: 3000,
      //       panelClass: ['snackbar-error']
      //     });
      //     return;
      //   }

      //   // Scenario 2: Already selected same slot count exceeds capacity
      //   const alreadySelectedCount = selectedSlots.filter(slot => slot.uniqueId === slotUniqueId).length;
      //   if (alreadySelectedCount >= this.selectedProduct.capacityPerVenue) {
      //     console.log("Scenario 2 - Already selected full");
      //     this.snackBar.open('The capacity for this slot is already full. Please choose a different slot.', 'Close', {
      //       duration: 3000,
      //       panelClass: ['snackbar-error']
      //     });
      //     return;
      //   }

      //   // Scenario 3: Safe to proceed with selection
      //   console.log("Scenario 3 - Slot selected");
      //   slotClicked.isSelected = true;
      //   slotClicked.availableSlots[0].isSelected = false;
      //   this.logicForAddRemove(slotClicked.availableSlots[0]);
      //   return;
      // }

      if (this.selectedProduct.capacityPerVenue > 1) {
        const slotUniqueId = slotClicked.availableSlots[0]?.uniqueId;

        // Check if this slot is already selected
        const alreadySelected = selectedSlots.some(slot => slot.uniqueId === slotUniqueId);
        if (alreadySelected) {
          console.log("Already selected this slot");
          this.snackBar.open('You have already requested this slot. Kindly review your booking queue to finalize the booking.', 'Close', {
            duration: 3000,
            panelClass: ['snackbar-error']
          });
          return;
        }

        // Check if booked slots already full
        if (Array.isArray(slotClicked.bookedSlots) && slotClicked.bookedSlots.length >= this.selectedProduct.capacityPerVenue) {
          console.log("Scenario 1 - Booked full");
          this.snackBar.open('This selected slot is fully booked!', 'Close', {
            duration: 3000,
            panelClass: ['snackbar-error']
          });
          return;
        }

        // Safe to proceed
        console.log("Scenario 3 - Slot selected");
        slotClicked.isSelected = true;
        slotClicked.availableSlots[0].isSelected = false;
        this.logicForAddRemove(slotClicked.availableSlots[0]);
        return;
      }

      // SINGLE SLOT CASE
      if (this.selectedProduct.capacityPerVenue <= 1) {
        slotClicked.isSelected = !slotClicked.isSelected;
        this.logicForAddRemove(slotClicked.availableSlots[0]);
      }
    }

    // Add to Cart logic
    // if (action == 'add') {
    //   if (this.selectedProduct.capacityPerVenue > 1) {
    //     const dialogRef = this.dialog.open(AddToCartComponent, {
    //       data: { slot: slotClicked, selectedProduct: this.selectedProduct }
    //     });

    //     dialogRef.afterClosed().subscribe(result => {
    //       if (result) {
    //         if (typeof result === 'number' && result > 0) {
    //           slotClicked.isSelected = true;

    //           // ✅ Step 2: Now add new ones using the existing loop
    //           // let i = 0;

    //           // const processWithDelay = () => {
    //           //   if (i < result) {
    //           //     if (slotClicked.availableSlots && slotClicked.availableSlots.length) {
    //           //       console.log("callllllllllllllll", slotClicked.availableSlots[0]);
    //           //       slotClicked.availableSlots[0].isSelected = false;
    //           //       this.logicForAddRemove(slotClicked.availableSlots[0]);
    //           //     }
    //           //     i++;
    //           //     setTimeout(processWithDelay, 500); // 500ms delay between each call
    //           //   }
    //           // };

    //           // processWithDelay(); // Start the loop

    //           if (slotClicked.availableSlots && slotClicked.availableSlots.length) {
    //             // 🔁 Create result number of slot objects
    //             const slotsToAdd = Array(result).fill(null).map(() => ({
    //               ...slotClicked.availableSlots[0],
    //               isSelected: true,
    //               isAvailable: false
    //             }));

    //             // 🚀 Use the bulk add function
    //             this.adminBookingSvc.addMultipleToSelectedSlots(slotsToAdd);
    //           }
    //         }
    //         return;
    //       }
    //     });
    //   } else {
    //     if (slotClicked.availableSlots && slotClicked.availableSlots.length) {
    //       slotClicked.isSelected = !slotClicked.isSelected;
    //       // slotClicked.availableSlots[0].isSelected = false;
    //       this.logicForAddRemove(slotClicked.availableSlots[0]);
    //     }
    //   }
    //   return;
    // }

    else if (action == 'delete') {
      if (slotClicked.availableSlots && slotClicked.availableSlots.length) {
        slotClicked.isSelected = false;
        slotClicked.isAvailable = true;
        console.log(slotClicked)
        this.logicForAddRemove(slotClicked.availableSlots[0]);
      }
      return;
    }
    else {
      this.logicForAddRemove(slotClicked);
    }
  }

  logicForAddRemove(slotData) {
    console.log("slotData", slotData)
    slotData.isSelected = !slotData.isSelected;
    slotData.isAvailable = !slotData.isAvailable;
    if (slotData.isSelected) {
      console.log("ADD...")
      this.adminBookingSvc.addToSelectedSlots(slotData);
    } else {
      console.log("removed!!!")
      this.adminBookingSvc.removeBulkFromSelectedSlots({ uniqueId: slotData.uniqueId });
    }
  }

  handleClick(event: MouseEvent, element: any, dateFormat: string, venueName: string): void {
    const slot = element[dateFormat][venueName];
    this.selectedSlot = slot;

    if (this.canOpenMenu(slot)) return; // Let menu handle interaction

    if (slot.isBooked) {
      this.showDetails(element, dateFormat, event, venueName);
    } else if (slot.isAvailable && !slot.isSelected) {
      this.addToCart(element, dateFormat, event, venueName);
    } else if (slot.isSelected) {
      this.removeFromCart(element, dateFormat, event, venueName);
    }
  }

  handleDblClick(event: MouseEvent, element: any, date: string, venueName: string): void {
    if (this.canOpenMenu(element[date][venueName])) {
      event.stopPropagation();
      return;
    }

    if (element[date][venueName].availableSlots?.length > 0) {
      this.onDblClick();
    }
  }

  // Handle double click on slot
  onDblClick() {
    this.isSingleClick = false;
    if (this.singleClickTimeout) {
      this.slotMenuTrigger.closeMenu();
      clearTimeout(this.singleClickTimeout);
      this.bookNow();
    }
  }

  // Open booking dialog
  bookNow() {
    this.dialog.open(BookSelectedSlotsComponent, {
      width: '600px',
      data: {}
    });
  }

  // Process and organize slot data
  formSlotData() {
    const sessionData = this.bookingData;
    if (!sessionData) {
      return;
    }

    sessionData.forEach((venueData: VenueBookingData) => {
      if (venueData.availableSlots && venueData.availableSlots.length > 0) {
        venueData.availableSlots.forEach((availableSlot) => {
          if (availableSlot.uniqueId) {
            const yearStrLength = 4;
            const dateLength = 10;
            const uniqueId = availableSlot.uniqueId;
            const indexOfFirstHyphen = uniqueId.indexOf('-');
            const dateKey = uniqueId.substr(indexOfFirstHyphen - yearStrLength, dateLength);
            const slotDate = new Date(dateKey);
            const startTime = availableSlot.startTime.split(':');
            slotDate.setHours(startTime[0]);
            slotDate.setMinutes(startTime[1]);
            const currentTime = new Date().getTime();
            if (slotDate.getTime() >= currentTime) {
              this.parseSlotData(availableSlot, 'available', venueData.venueName);
            }
          }
        });
      }

      if (venueData.bookedSlots && venueData.bookedSlots.length > 0) {
        venueData.bookedSlots.forEach((bookedSlot) => {
          this.parseSlotData(bookedSlot, 'Booked', venueData.venueName);
        });
      }
    });
  }

  // Parse and store slot data
  parseSlotData(data, type, venueName) {
    if (data.startTime && data.endTime) {
      const yearStrLength = 4;
      const dateLength = 10;
      const uniqueId = data.uniqueId;
      let indexOfFirstHyphen;
      let dateKey;
      if (uniqueId) {
        indexOfFirstHyphen = uniqueId.indexOf('-');
        dateKey = 'date_' + uniqueId.substr(indexOfFirstHyphen - yearStrLength, dateLength);
      } else if (type === 'Booked') {
        dateKey = 'date_' + this.getDateStrFromTime(data.bookingDate);
      }

      if (this.slotDetails[data.startTime] && this.slotDetails[data.startTime][dateKey]) {
        const slotData = this.slotDetails[data.startTime][dateKey];
        this.addSlotData(data, slotData, type, dateKey, venueName);
      } else {
        const slotData = {
          availableSlots: [],
          bookedSlots: []
        };
        if (!this.slotDetails[data.startTime]) {
          this.slotDetails[data.startTime] = {};
        }
        this.slotDetails[data.startTime][dateKey] = this.addSlotData(data, slotData, type, dateKey, venueName);
      }
    }
  }

  // Add slot data to the slot details object
  addSlotData(data, slotData, type, dateKey, venueName) {
    const selectedSlots = JSON.parse(sessionStorage.getItem('selectedSlots'));
    let matchingSlot;
    if (selectedSlots && selectedSlots.length > 0) {
      matchingSlot = selectedSlots.find(selectedSlot => {
        return selectedSlot.uniqueId === data.uniqueId;
      });
    }

    if (!slotData[venueName]) {
      slotData[venueName] = {
        availableSlots: [],
        bookedSlots: []
      };
    }

    if (type === 'available') {
      slotData[venueName].availableSlots.push({
        displayLabel: 'Available',
        isSelected: matchingSlot ? matchingSlot.isSelected : false,
        isAvailable: matchingSlot ? matchingSlot.isAvailable : true,
        price: Math.round(data.price),
        slotId: this.getSlotId(data, dateKey),
        tenantId: data.tenantId,
        productId: data.productId,
        productName: this.selectedProduct.name,
        venueName: venueName,
        venueId: data.venueId,
        startTime: data.startTime,
        endTime: data.endTime,
        date: dateKey,
        uniqueId: data.uniqueId
      });
    }

    if (type === 'Booked') {
      slotData[venueName].bookedSlots.push({
        displayLabel: 'Booked',
        isAvailable: false,
        isSelected: false,
        price: data.amount,
        slotId: this.getSlotId(data, dateKey),
        tenantId: data.tenantId,
        productId: data.productId,
        productName: this.selectedProduct.name,
        venueName: venueName,
        venueId: data.venueId,
        customerName: data.username,
        customerEmail: data.email,
        customerPhone: data.phoneNumber,
        bookingId: data.confirmationId,
        bookingDate: data.bookingDate,
        startTime: data.startTime,
        endTime: data.endTime,
        paymentType: data.paymentType,
        bookedOn: data.createdAt,
        notes: data.notes,
        isBooked: true,
        bookingStatus: data.bookingStatus,
        config: data.config
      });
    }

    return slotData;
  }

  // Get date string from timestamp
  getDateStrFromTime(timeValue) {
    const date = new Date(timeValue);
    return date.getFullYear() + '-' + this.addZeroIfRequired(date.getMonth() + 1) + '-' +
      this.addZeroIfRequired(date.getDate());
  }

  // Get array of day columns for the grid
  getDayColumns() {
    const dayColumns = [];
    this.dayColumnsObj = [];
    for (let i = 0; i < this.dateRange; i++) {
      const date = this.addXNumberOfDays(this.selectedDate, i);
      const dateTime = date.getTime();
      const dateFormat = 'date_' + this.getDateFormatWithSeparator(date, '-');
      this.dayColumnsObj.push({
        dateTime: dateTime,
        dateFormat: dateFormat
      });
      dayColumns.push(dateFormat);
    }
    return dayColumns;
  }

  // Format date with separator
  getDateFormatWithSeparator(date: Date, separator = '-') {
    const year = date.getFullYear();
    let month: any = date.getMonth() + 1;
    let day: any = date.getDate();

    if (day < 10) {
      day = '0' + day;
    }
    if (month < 10) {
      month = '0' + month;
    }
    return year + separator + month + separator + day;
  }

  // Get formatted header for day column
  getDayHeader(dateTime) {
    if (dateTime) {
      const dateObj = new Date(dateTime);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Reset time part for comparison
      today.setHours(0, 0, 0, 0);
      tomorrow.setHours(0, 0, 0, 0);
      dateObj.setHours(0, 0, 0, 0);

      if (dateObj.getTime() === today.getTime()) {
        return 'Today';
      } else if (dateObj.getTime() === tomorrow.getTime()) {
        return 'Tomorrow';
      } else {
        return dateObj.getDate() + ' ' + this.months[dateObj.getMonth()];
      }
    }
    return '';
  }

  // Get weekday name for the date
  getWeekDay(dateTime) {
    if (dateTime) {
      const dateObj = new Date(dateTime);
      return this.weekdays[dateObj.getDay()]
    }
    return '';
  }


  getDayClass(dateTime: string): string {
    const date = new Date(dateTime);
    const day = date.getDay();
    if (day === 6) {
      return 'saturday';
    } else if (day === 0) {
      return 'sunday';
    }
    return '';
  }

  setAllDayHoursInSource() {
    this.dataSource = [];

    const sessions = this.selectedProduct?.sessions || [];
    if (!sessions.length) return;

    const interval = this.slotInterval || 60; // Default to 60 minutes if not provided
    const minsPerDay = 24 * 60; // Total minutes in a day (24 hours)
    const sessionSlotSet = new Set<number>();

    sessions.forEach(session => {
      let start = this.timeToMinutes(this.convertTo24Hr(session.startTime));
      let end = this.timeToMinutes(this.convertTo24Hr(session.endTime));

      let current = start;

      if (end === 0) end = minsPerDay;
      if (end <= start) {
        // Handle sessions that span midnight, add slots till midnight
        for (let current = start; current < minsPerDay; current += interval) {
          sessionSlotSet.add(current);
        }
        // If needed, add slots from 0 to end on the next day (optional, based on your app logic)
        // For your use case, maybe ignore this to keep slots within a single day only
      } else {
        for (let current = start; current < end; current += interval) {
          sessionSlotSet.add(current);
        }
      }

      // Add only slots that match the interval (e.g., 60 mins)
      // while (current <= end) {
      //   sessionSlotSet.add(current);
      //   current += interval;
      // }
    });

    // Convert sessionSlotSet to an array of sorted dateTimes
    const sessionSlots = Array.from(sessionSlotSet).sort((a, b) => a - b);

    // Now fill in the gaps between session slots
    const finalSlots = [];
    let previousSlot = 0;

    // Ensure we have slots from the beginning of the day to the first session slot
    for (let mins = 0; mins < minsPerDay; mins += interval) {
      const now = new Date();
      const currentMins = now.getHours() * 60 + now.getMinutes();
      const isPast = mins < currentMins;
      if (sessionSlots.length === 0 || mins < sessionSlots[0]) {
        finalSlots.push({ dateTime: `${Math.floor(mins / 60)}:${(mins % 60).toString().padStart(2, '0')}`, isAvailable: !isPast });
      }
    }

    // Add the session slots and check for gaps
    sessionSlots.forEach((slot, index) => {
      if (previousSlot && slot - previousSlot > interval) {
        // If there is a gap, add the missing slots
        let gapStart = previousSlot + interval;
        while (gapStart < slot) {
          finalSlots.push({ dateTime: `${Math.floor(gapStart / 60)}:${(gapStart % 60).toString().padStart(2, '0')}` });
          gapStart += interval;
        }
      }
      finalSlots.push({ dateTime: `${Math.floor(slot / 60)}:${(slot % 60).toString().padStart(2, '0')}` });
      previousSlot = slot;
    });

    // Ensure the last slot to cover up to the end of the day
    let endOfDaySlot = previousSlot + interval;
    while (endOfDaySlot < minsPerDay) {
      finalSlots.push({ dateTime: `${Math.floor(endOfDaySlot / 60)}:${(endOfDaySlot % 60).toString().padStart(2, '0')}` });
      endOfDaySlot += interval;
    }

    this.dataSource = finalSlots;
    this.origDataSource = finalSlots;
  }

  // Converts "4:30 AM" or "5:00 PM" to "4:30" or "17:00"
  convertTo24Hr(time: string): string {
    const [timePart, modifier] = time.split(' ');
    let [hours, minutes] = timePart.split(':').map(Number);

    if (modifier === 'PM' && hours !== 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }

  timeToMinutes(timeStr: string): number {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  }

  // Format time string to 12-hour format
  getFormattedTime(timeString) {
    let formattedTime = '';
    const splitTimeString = timeString.split(':');
    let timeValue = parseInt(splitTimeString[0], 10);
    const minsValue = parseInt(splitTimeString[1], 10);

    if (timeValue >= 12) {
      if (timeValue !== 12) {
        timeValue = timeValue % 12;
      }
      if (minsValue === 0) {
        formattedTime = timeValue + ' PM';
      } else {
        formattedTime = timeValue + ':' + splitTimeString[1] + ' PM';
      }
    } else {
      if (timeValue === 0) {
        timeValue = 12;
      }
      if (minsValue === 0) {
        formattedTime = timeValue + ' AM';
      } else {
        formattedTime = timeValue + ':' + splitTimeString[1] + ' AM';
      }
    }
    return formattedTime;
  }

  // 1) is this calendar‐cell’s date before today?
  /** dateKey comes in as "date_YYYY‑MM‑DD" → parse the YYYY‑MM‑DD portion */
  isPastDate(dateKey: string): boolean {
    if (!dateKey) return false;

    const d = dateKey.startsWith('date_')
      ? dateKey.slice(5)           // remove "date_"
      : dateKey;

    const cell = new Date(d);
    const today = new Date();

    // zero‑out time portion for an “all‑day” compare
    cell.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return cell < today;
  }

  // 2) is this one slot’s time before now? (accepts "4:00" or "04:00" or "16:00")
  isPastTime(timeStr: string): boolean {
    if (!timeStr) return false;
    // split into [hour, minute] and convert
    const parts = timeStr.split(':').map(p => parseInt(p, 10));
    if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return false;
    const [hours, mins] = parts;
    const slotMins = hours * 60 + mins;
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    return slotMins < nowMins;
  }

  // 3) combine date & time into “past slot?”
  isPastSlot(dateKey: string, startTime: string): boolean {
    if (this.isPastDate(dateKey)) return true;

    // if it’s exactly today compare times
    const cellDate = (dateKey.startsWith('date_') ? dateKey.slice(5) : dateKey);
    if (new Date(cellDate).toDateString() === new Date().toDateString()) {
      return this.isPastTime(startTime);
    }

    return false;  // future date
  }

  // 4️⃣ update your getSlotClass to force 'unavailable-slot' for past cells
  getSlotClass(slotData: any, dateKey: string): string {
    if (!slotData) {
      return 'unavailable-slot';
    }

    // determine startTime from whichever array has it
    const st = slotData.availableSlots?.[0]?.startTime
      || slotData.bookedSlots?.[0]?.startTime;

    if (st && this.isPastSlot(dateKey, st)) {
      return 'booked-slot';
    }

    if (slotData.isSelected) {
      return 'selected-slot';
    } else if (slotData.isBooked) {
      return 'booked-slot';
    } else if (slotData.isPartiallyBooked) {
      return 'partially-booked-slot';
    } else if (slotData.isAvailable) {
      return 'available-slot';
    } else {
      return 'unavailable-slot';
    }
  }

  updateDataSource() {
    this.dayColumns.forEach((column) => {
      this.dataSource.forEach(rowData => {
        // Ensure rowData[column] exists
        if (!rowData[column]) {
          rowData[column] = {};
        }

        this.venues.forEach(venue => {
          // Ensure rowData[column][venue.name] exists
          if (!rowData[column][venue.name]) {
            rowData[column][venue.name] = {
              displayLabel: '--',
              isAvailable: false,
              isSelected: false,
              isBooked: false,
              isPartiallyBooked: false,
              availableSlots: [],
              bookedSlots: [],
              selectedSlots: []
            };
          }

          const slotData = this.slotDetails[rowData.dateTime]?.[column]?.[venue.name];
          if (slotData) {
            const capacity = this.selectedProduct?.capacityPerVenue || 1;

            // Assign availableSlots
            if (slotData.availableSlots?.length > 0) {
              const selectedSlots = slotData.availableSlots.filter(slot => slot.isSelected);
              rowData[column][venue.name].availableSlots = slotData.availableSlots.map(slot => ({
                ...slot,
                isAvailable: !slot.isSelected,
              }));

              if (selectedSlots.length > 0) {
                rowData[column][venue.name].isSelected = true;
              }
            }

            // Assign bookedSlots
            if (slotData.bookedSlots?.length > 0) {
              const bookedCount = slotData.bookedSlots.length;
              const capacity = this.selectedProduct?.capacityPerVenue || 1;

              const isAnyBooked = slotData.bookedSlots.some(slot => slot.isBooked);
              rowData[column][venue.name].bookedSlots = slotData.bookedSlots.map(slot => ({
                ...slot,
                isAvailable: false,
              }));

              if (isAnyBooked) {
                rowData[column][venue.name].isBooked = true;
              }

              // Capacity handling (partial or full)
              if (capacity > 1) {
                if (bookedCount === capacity) {
                  rowData[column][venue.name].isBooked = true;
                } else {
                  rowData[column][venue.name].isPartiallyBooked = true;
                  rowData[column][venue.name].isAvailable = true;
                  rowData[column][venue.name].isBooked = false;
                }
              } else {
                rowData[column][venue.name].isBooked = true;
              }
            } else {
              // No bookings, but slots may still be available
              const capacity = this.selectedProduct?.capacityPerVenue || 1;
              if (capacity > 1 && slotData.availableSlots?.length > 0) {
                rowData[column][venue.name].isAvailable = true;
              }
            }

            // Re-check isAvailable based on remaining capacity
            const bookedCount = slotData.bookedSlots?.length || 0;
            if (bookedCount < capacity) {
              rowData[column][venue.name].isAvailable = true;
            }
          }
        });

      });
    });
  }

  // Generate unique slot ID
  getSlotId(data, dateKey): string {
    //console.log("get Slot Id", data, dateKey);
    return data.tenantId + '_' + data.productId   + '_' + dateKey.slice(5) + '_' +
      data.startTime + '_' + data.endTime + '_' + data.venueId;
  }

  canOpenMenu(slot: any): boolean {
    return this.selectedProduct?.capacityPerVenue > 1
      && !!slot &&
      (slot.isAvailable || slot.isBooked || slot.isSelected);
  }

  openSlotMenu(event: MouseEvent, element: any, dateFormat: string, venueName: string) {
    event.stopPropagation(); // Prevent event bubbling

    // Check if a double-click is coming
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
      return;
    }

    // Set a timeout for single click
    this.clickTimeout = setTimeout(() => {
      this.selectedSlot = element[dateFormat][venueName];
      this.slotMenuTrigger.openMenu();
      this.clickTimeout = null;
    }, 250); // 250ms delay to allow for `dblclick`
  }

  showDetails(element, dataKey, event, venue) {
    this.onSingleClick(element, dataKey, event, venue, 'view');
  }

  addToCart(element, dataKey, event, venue) {
    this.onSingleClick(element, dataKey, event, venue, 'add');
  }

  removeFromCart(element, dataKey, event, venue) {
    this.onSingleClick(element, dataKey, event, venue, 'delete');
  }

  handleMouseDown(element: any, dateFormat: string, event: MouseEvent, venueName: string): void {
    const slot = element[dateFormat][venueName];

    if (!this.canOpenMenu(slot)) {
      if (slot.isBooked) {
        this.showDetails(element, dateFormat, event, venueName);
      } else if (slot.isAvailable && !slot.isSelected) {
        this.addToCart(element, dateFormat, event, venueName);
      } else if (slot.isSelected) {
        this.removeFromCart(element, dateFormat, event, venueName);
      }
    }
    // If menu should open, default behavior via [matMenuTriggerFor]
  }

  handleDoubleClick(element: any, dateFormat: string, event: MouseEvent, venueName: string): void {
    const slot = element[dateFormat][venueName];

    if (this.canOpenMenu(slot)) {
      this.onDblClick(); // if any logic is tied to double click
    } else {
      // optional: maybe call same logic as single click?
    }
  }

  remainingSlots(bookedSlots) {
    let remainingSlot = 0;
    if (this.selectedProduct.capacityPerVenue && bookedSlots && bookedSlots.length) {
      remainingSlot = this.selectedProduct.capacityPerVenue - bookedSlots.length;
    }
    return remainingSlot;
  }

  // inside your component class

  /** return true if the given time (e.g. "14:30" or "2:30 PM") is before now */
  /** returns true if that slot’s startTime (in "HH:mm") is before now */
  isPast(timeStr: string): boolean {
    if (!timeStr) return false;
    const [hours, mins] = timeStr.split(':').map(n => +n);
    const slotMins = hours * 60 + mins;
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    return slotMins < nowMins;
  }

  // Cleanup subscriptions on component destroy
  ngOnDestroy() {
    if (this.bookingSlotsSubscription) {
      this.bookingSlotsSubscription.unsubscribe();
    }
    if (this.reloadDataSubscription) {
      this.reloadDataSubscription.unsubscribe();
    }
    if (this.selectedSlotRemovedSubscription) {
      this.selectedSlotRemovedSubscription.unsubscribe();
    }
    if (this.pageRefreshSubscriber) {
      this.pageRefreshSubscriber.unsubscribe();
    }
  }
}
